<!--
  One folder in the rail, and everything under it.

  Recursive: it imports itself, which is how a tree of unknown depth renders
  without the component knowing how deep it goes. The server bounds the depth at
  10, so the recursion is bounded by the data rather than by a guard here.

  TASK-042 §2: this component carries NO ACTIONS. The Rename and Delete
  mini-buttons and the inline rename form are gone -- moved, not deleted, to the
  toolbar in the content pane (§3a). The rail is navigation.

  TASK-042 §9: each row is a `treeitem` and the tree has ONE tab stop.

  1. **The row is the focusable thing**, with `tabindex` roving between rows, so
     Tab enters the rail once rather than once per folder. Arrow keys move
     within it, which is the standard tree pattern.
  2. **The twisty stays a separate control for the POINTER, and is hidden from
     assistive technology.** Clicking a folder SELECTS it; clicking the twisty
     expands it. One button doing both means a person cannot open a folder
     without also changing the list, which is the thing they were looking at
     when they reached for it. A keyboard does not need the twisty at all --
     Right expands and Left collapses -- so it is `aria-hidden` with
     `tabindex="-1"`: a second announced control per row would be noise, and a
     second tab stop per row is exactly what the roving tabindex removes.
  3. **The indent caps at five levels.** 12px per level, then flat. Ten levels
     of indent inside a 180px rail is a column of ellipses, and past five it is
     the breadcrumb above the list that tells you where you are.

  TASK-042 §6b: a row is a DROP TARGET, and that is not an action. It adds no
  control, no label and nothing to click; with `zone` absent -- which is the
  one-word `TREE_ACCEPTS_DROPS` change -- every handler below is inert and the
  markup is identical. Nothing appears at rest: a row changes only while a
  document is actually being dragged over it.
-->
<script lang="ts">
  import Icon from './Icon.svelte'
  import type { FolderNode, View } from './doclist-types'
  import { sameView } from './doclist-types'
  import type { DropZone } from '../drop'
  import { acceptDrag, leftTarget } from '../drop'
  import Self from './FolderBranch.svelte'

  let {
    node,
    level,
    view,
    expanded,
    active,
    ontoggle,
    onchoose,
    onkey,
    onwarm,
    zone,
    over,
  }: {
    node: FolderNode
    /** 1 is the Root node, so a top-level folder is 2. Becomes `aria-level`. */
    level: number
    view: View
    expanded: Set<string>
    /** The one row carrying `tabindex="0"`. `null` is the Root node. */
    active: string | null
    ontoggle: (id: string, open?: boolean) => void
    onchoose: (id: string | null) => void
    onkey: (event: KeyboardEvent, id: string | null) => void
    /** Pointed at. The screen prefetches this folder's documents; see the rail. */
    onwarm?: (id: string | null) => void
    /** Absent when the tree is not a drop target (§6b, decision A). */
    zone?: DropZone
    /** The one row a drag is currently over. `null` is the Root row. */
    over?: string | null
  } = $props()

  const indent = $derived(`${Math.min(level - 1, 5) * 12}px`)
  const open = $derived(expanded.has(node.id))
  const selected = $derived(sameView(view, { kind: 'folder', id: node.id }))

  /*
   * Nothing is conveyed by colour alone (§9): a target that cannot take the
   * drop changes its outline STYLE to dashed as well as its colour, and the
   * reason is said out loud in the screen's `dragNotice` region.
   */
  const aimed = $derived(Boolean(zone?.active) && over === node.id)
  const welcome = $derived(aimed && (zone?.accepts(node.id) ?? false))
  const refuses = $derived(aimed && !welcome)
</script>

<li role="none">
  <div
    class="rail-row"
    role="treeitem"
    data-tree-id={node.id}
    aria-level={level}
    aria-selected={selected ? 'true' : undefined}
    aria-expanded={node.children.length > 0 ? open : undefined}
    tabindex={active === node.id ? 0 : -1}
    class:drop-welcome={welcome}
    class:drop-refuses={refuses}
    style={`padding-left:${indent}`}
    onclick={() => onchoose(node.id)}
    onkeydown={(event) => onkey(event, node.id)}
    onpointerenter={() => onwarm?.(node.id)}
    ondragenter={(event) => {
      if (!zone) return
      if (acceptDrag(event, zone.accepts(node.id))) zone.aim(node.id, true)
    }}
    ondragover={(event) => {
      // Every frame of the hover, because a `dragover` that does not
      // preventDefault is a target the browser stops offering.
      if (zone) acceptDrag(event, zone.accepts(node.id))
    }}
    ondragleave={(event) => {
      if (zone && leftTarget(event)) zone.aim(node.id, false)
    }}
    ondrop={(event) => {
      if (!zone || !acceptDrag(event, zone.accepts(node.id))) return
      // Stop aiming before dropping: a `dragleave` does not fire on the target
      // a drop landed on, so without this the row stays the remembered one.
      zone.aim(node.id, false)
      zone.drop(node.id, event.dataTransfer)
    }}
  >
    {#if node.children.length > 0}
      <button
        type="button"
        class="rail-twisty"
        class:open
        tabindex="-1"
        aria-hidden="true"
        onclick={(event) => {
          // Or the click reaches the row behind it and selects the folder as
          // well as expanding it, which is the one thing the separate control
          // exists to prevent.
          event.stopPropagation()
          ontoggle(node.id)
        }}
      >
        <Icon name="chevron" size="0.95rem" />
      </button>
    {:else}
      <span class="rail-twisty rail-twisty-empty" aria-hidden="true"></span>
    {/if}

    <Icon name={open && node.children.length > 0 ? 'folder-open' : 'folder'} />
    <span class="rail-name">{node.name}</span>
    <span class="rail-count">{node.docCount}</span>
  </div>

  {#if open && node.children.length > 0}
    <ul class="rail-list" role="group">
      {#each node.children as child (child.id)}
        <Self
          node={child}
          level={level + 1}
          {view}
          {expanded}
          {active}
          {ontoggle}
          {onchoose}
          {onkey}
          {onwarm}
          {zone}
          {over}
        />
      {/each}
    </ul>
  {/if}
</li>

<style>
  .rail-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .rail-row {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
    padding: 0.4rem 0.5rem;
    border-radius: 0.375rem;
    cursor: pointer;
  }

  .rail-row:hover {
    background: var(--color-plinth, #f1ece4);
  }

  .rail-row[aria-selected='true'] {
    background: var(--color-plinth, #f1ece4);
    font-weight: 600;
  }

  /*
   * The drop affordance, and the whole of it: a 2px outline and a tint. It
   * exists only while something is being dragged over this row, so the rail
   * looks identical at rest -- which is the property §6b rests on.
   *
   * `outline` rather than `border`, because a border would change the row's
   * box and shuffle the tree under the pointer mid-drag.
   */
  .rail-row.drop-welcome,
  .rail-row.drop-refuses {
    transition: background-color 0.12s ease, outline-color 0.12s ease;
  }

  .rail-row.drop-welcome {
    outline: 2px solid var(--color-ink, #211d18);
    outline-offset: -2px;
    background: var(--color-plinth, #f1ece4);
  }

  /* Dashed, not merely red: nothing on this screen is conveyed by colour
     alone, and the reason is also said out loud in `dragNotice`. */
  .rail-row.drop-refuses {
    outline: 2px dashed var(--color-red-deep, #c01030);
    outline-offset: -2px;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    .rail-row.drop-welcome,
    .rail-row.drop-refuses {
      transition: none;
    }
  }

  .rail-twisty {
    flex: 0 0 auto;
    width: 1.35rem;
    height: 1.35rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    color: var(--color-ink-soft, #6e665c);
    font: inherit;
    line-height: 1;
    cursor: pointer;
  }

  /* One path at two angles rather than two glyphs. */
  .rail-twisty.open :global(.icon-chevron) {
    rotate: 90deg;
  }

  .rail-twisty:hover {
    color: var(--color-ink, #211d18);
  }

  .rail-twisty-empty {
    cursor: default;
  }

  .rail-name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rail-count {
    flex: 0 0 auto;
    font-size: var(--text-meta, 0.8125rem);
    color: var(--color-ink-soft, #6e665c);
    font-variant-numeric: tabular-nums;
  }
</style>
